import React from 'react';

/**
 * SwimLoader — Lapistics brand page-loading indicator.
 *
 * A CSS-only swimmer silhouette glides across animated pool lane ropes.
 * Renders in under 1ms (no JS animation loop, pure keyframes).
 * Shown via DelayedSuspense in App.js — only visible if a lazy page
 * takes longer than 200 ms to load.
 */
const SwimLoader = () => (
  <div style={{
    position : 'fixed', inset: 0, zIndex: 9999,
    display  : 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, #012F3F 0%, #011d2b 55%, #013040 100%)',
    fontFamily: "'Outfit', sans-serif",
  }}>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@700;900&display=swap');

      @keyframes ropeScroll {
        from { transform: translateX(0); }
        to   { transform: translateX(-50%); }
      }

      @keyframes swimAcross {
        0%   { transform: translateX(-120px) scaleX(1); opacity: 0; }
        8%   { opacity: 1; }
        46%  { transform: translateX(calc(50vw - 40px)) scaleX(1); opacity: 1; }
        54%  { transform: translateX(calc(50vw - 40px)) scaleX(-1); opacity: 1; }
        92%  { opacity: 1; }
        100% { transform: translateX(calc(100vw + 120px)) scaleX(-1); opacity: 0; }
      }

      @keyframes flipSpin {
        0%,44%   { transform: rotate(0deg); }
        50%      { transform: rotate(180deg); }
        56%,100% { transform: rotate(0deg); }
      }

      @keyframes bubbleRise1 { 0%{transform:translateY(0) scale(1);opacity:.7} 100%{transform:translateY(-38px) scale(.4);opacity:0} }
      @keyframes bubbleRise2 { 0%{transform:translateY(0) scale(1);opacity:.5} 100%{transform:translateY(-30px) scale(.3);opacity:0} }
      @keyframes bubbleRise3 { 0%{transform:translateY(0) scale(1);opacity:.6} 100%{transform:translateY(-44px) scale(.5);opacity:0} }

      @keyframes lapPulse {
        0%,100%{ opacity:.9; transform:scale(1); }
        50%    { opacity:.5; transform:scale(.92); }
      }

      @keyframes dotBounce {
        0%,80%,100%{ transform:translateY(0);   opacity:.4 }
        40%        { transform:translateY(-6px); opacity:1  }
      }

      .swl-pool {
        position: relative;
        width: 100vw;
        height: 80px;
        overflow: hidden;
      }

      .swl-rope {
        position : absolute;
        left     : 0;
        width    : 200%;
        height   : 12px;
        display  : flex;
        align-items: center;
        animation: ropeScroll 2.4s linear infinite;
      }
      .swl-rope-half {
        display    : flex;
        width      : 50%;
        align-items: center;
      }
      .swl-rope-ball {
        width : 10px;
        height: 10px;
        border-radius: 50%;
        flex-shrink: 0;
        margin: 0 3px;
      }
      .swl-rope-line {
        flex  : 1;
        height: 1.5px;
        background: rgba(0,109,119,0.35);
      }

      .swl-lane-line {
        position: absolute;
        left: 0; right: 0;
        height: 1px;
        background: rgba(0,109,119,0.1);
      }

      .swl-figure {
        position       : absolute;
        bottom         : 20px;
        left           : 0;
        animation      : swimAcross 3s cubic-bezier(0.45,0,0.55,1) infinite;
        transform-origin: center center;
      }
      .swl-flip {
        animation      : flipSpin 3s cubic-bezier(0.45,0,0.55,1) infinite;
        transform-origin: center center;
      }

      .swl-bubble { position: absolute; border-radius: 50%; }
      .swl-b1 { width:5px;height:5px; bottom:26px; left:30px; background:rgba(0,109,119,.7); animation: bubbleRise1 0.8s ease-out 1.4s infinite; }
      .swl-b2 { width:3px;height:3px; bottom:24px; left:40px; background:rgba(232,197,71,.6); animation: bubbleRise2 0.9s ease-out 1.55s infinite; }
      .swl-b3 { width:4px;height:4px; bottom:28px; left:48px; background:rgba(0,109,119,.5); animation: bubbleRise3 0.7s ease-out 1.65s infinite; }

      .swl-logo { animation: lapPulse 1.6s ease-in-out infinite; }

      .swl-dot {
        display: inline-block;
        width: 5px; height: 5px;
        border-radius: 50%;
        background: #E8C547;
        margin: 0 3px;
      }
      .swl-dot:nth-child(1){ animation: dotBounce 1.2s ease-in-out 0s   infinite; }
      .swl-dot:nth-child(2){ animation: dotBounce 1.2s ease-in-out 0.2s infinite; }
      .swl-dot:nth-child(3){ animation: dotBounce 1.2s ease-in-out 0.4s infinite; }
    `}</style>

    {/* ── Logo ── */}
    <div className="swl-logo" style={{ textAlign: 'center', marginBottom: 32 }}>
      <img
        src="/logo512.png"
        alt="Lapistics"
        width={64}
        height={64}
        style={{ display: 'block', margin: '0 auto 10px', objectFit: 'contain' }}
      />
      <div style={{
        fontWeight: 900, fontSize: '1.5rem',
        letterSpacing: '-.02em', color: '#F5EFE2',
        fontFamily: "'Outfit', sans-serif",
      }}>Lapistics</div>
    </div>

    {/* ── Pool lane strip ── */}
    <div className="swl-pool">

      {/* lane divider lines (subtle) */}
      {[15, 40, 65].map(top => (
        <div key={top} className="swl-lane-line" style={{ top }} />
      ))}

      {/* three lane ropes at different vertical positions + stagger */}
      {[
        { top: 12, colors: ['#006D77', 'rgba(0,109,119,0.25)'], delay: '0s'   },
        { top: 37, colors: ['#E8C547', 'rgba(232,197,71,0.25)'], delay: '0.4s' },
        { top: 62, colors: ['#006D77', 'rgba(0,109,119,0.25)'], delay: '0.8s' },
      ].map(({ top, colors, delay }) => (
        <div key={top} className="swl-rope" style={{ top, animationDelay: delay }}>
          {[0, 1].map(r => (
            <div key={r} className="swl-rope-half">
              {Array.from({ length: 18 }).map((_, j) => (
                <React.Fragment key={j}>
                  <div className="swl-rope-line" />
                  <div className="swl-rope-ball" style={{
                    background: j % 2 === 0 ? colors[0] : colors[1],
                    opacity   : j % 2 === 0 ? 0.85 : 0.4,
                  }} />
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      ))}

      {/* Swimmer SVG silhouette */}
      <div className="swl-figure">
        <div className="swl-flip">
          <svg width="68" height="32" viewBox="0 0 68 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* torso/body */}
            <ellipse cx="30" cy="17" rx="20" ry="5.5" fill="#006D77" opacity="0.92" />
            {/* head */}
            <circle cx="51" cy="14" r="6.5" fill="#006D77" opacity="0.95" />
            {/* swim cap stripe */}
            <path d="M48 9 Q53 7 57 11" stroke="#E8C547" strokeWidth="1.8" strokeLinecap="round" opacity="0.85" />
            {/* goggle lens glint */}
            <ellipse cx="53" cy="13" rx="2.5" ry="1.6" fill="#E8C547" opacity="0.6" />
            {/* leading arm */}
            <path d="M49 10 Q60 3 65 8" stroke="#006D77" strokeWidth="3.5" strokeLinecap="round" opacity="0.88" />
            {/* trailing arm (streamlined) */}
            <path d="M22 17 Q14 14 10 17" stroke="#006D77" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
            {/* kick legs */}
            <path d="M11 15 Q4  9  1 15" stroke="#006D77" strokeWidth="2.5" strokeLinecap="round" opacity="0.78" />
            <path d="M11 19 Q4 24  1 19" stroke="#006D77" strokeWidth="2"   strokeLinecap="round" opacity="0.5"  />
          </svg>
        </div>
        {/* bubble trail */}
        <div className="swl-bubble swl-b1" />
        <div className="swl-bubble swl-b2" />
        <div className="swl-bubble swl-b3" />
      </div>
    </div>

    {/* ── Loading label ── */}
    <div style={{ marginTop: 28, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{
        fontSize: '0.78rem', fontWeight: 600, letterSpacing: 2.5,
        color: 'rgba(245,239,226,0.45)', textTransform: 'uppercase',
        fontFamily: "'Outfit', sans-serif",
      }}>Loading</span>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span className="swl-dot" />
        <span className="swl-dot" />
        <span className="swl-dot" />
      </div>
    </div>
  </div>
);

export default SwimLoader;
